import { fetchTrainingSessions, type TrainingSession } from '../services/progress';
import { fetchAchievementUnlocksFromCloud, syncAchievementUnlocksToCloud } from '../services/achievements';
import { calculateCurrentStreak, calculateTotalXp } from './trainingStats';
import { hasFirebaseConfig } from './firebase';
import type { AppLanguage, TrainingMode } from '../types';

const ACHIEVEMENT_UNLOCKS_STORAGE_PREFIX = 'vm-achievement-unlocks:v1:';

const TRAINING_MODES = ['typing', 'multiple-choice', 'flashcards', 'speed-verbs'] as const;

export type AchievementCategory = 'starter' | 'consistency' | 'progress' | 'volume' | 'specialization';
export type AchievementMetric = 'sessions_total' | 'current_streak_days' | 'xp_total' | 'sessions_by_mode';
export type AchievementCelebrationVariant = 'toast' | 'card' | 'modal';
export type AchievementIcon = 'spark' | 'flame' | 'zap' | 'trophy' | 'keyboard' | 'cards' | 'pointer' | 'timer';
export type AchievementScopeKey = 'all' | TrainingMode;

export interface AchievementRewardDefinition {
  badgeLabel?: {
    en: string;
    uk: string;
  };
  unlockLabel?: {
    en: string;
    uk: string;
  };
}

export interface AchievementTierDefinition {
  tier: number;
  threshold: number;
  celebration: AchievementCelebrationVariant;
  priority: number;
  icon: AchievementIcon;
  reward?: AchievementRewardDefinition;
}

export interface AchievementDefinition {
  id: string;
  category: AchievementCategory;
  metric: AchievementMetric;
  scopeKind: 'all' | 'per-mode';
  tiers: AchievementTierDefinition[];
}

export interface AchievementUnlockRecord {
  unlockKey: string;
  definitionId: string;
  tier: number;
  scopeKey: AchievementScopeKey;
  category: AchievementCategory;
  celebration: AchievementCelebrationVariant;
  priority: number;
  icon: AchievementIcon;
  threshold: number;
  value: number;
  unlockedAt: string;
}

export interface AchievementRewardView {
  label: string;
  value: string;
}

export interface AchievementViewModel {
  unlockKey: string;
  title: string;
  description: string;
  celebration: AchievementCelebrationVariant;
  priority: number;
  icon: AchievementIcon;
  category: AchievementCategory;
  scopeKey: AchievementScopeKey;
  rewardLines: AchievementRewardView[];
}

export interface AchievementProcessResult {
  unlocks: AchievementUnlockRecord[];
}

interface AchievementStats {
  sessionsTotal: number;
  xpTotal: number;
  currentStreakDays: number;
  sessionsByMode: Record<TrainingMode, number>;
}

const ACHIEVEMENT_DEFINITIONS: AchievementDefinition[] = [
  {
    id: 'first_training',
    category: 'starter',
    metric: 'sessions_total',
    scopeKind: 'all',
    tiers: [
      {
        tier: 1,
        threshold: 1,
        celebration: 'modal',
        priority: 100,
        icon: 'spark',
        reward: {
          badgeLabel: {
            en: 'First Step',
            uk: 'Перший крок',
          },
        },
      },
    ],
  },
  {
    id: 'streak_days',
    category: 'consistency',
    metric: 'current_streak_days',
    scopeKind: 'all',
    tiers: [
      { tier: 1, threshold: 3, celebration: 'toast', priority: 35, icon: 'flame' },
      {
        tier: 2,
        threshold: 7,
        celebration: 'card',
        priority: 55,
        icon: 'flame',
        reward: {
          badgeLabel: {
            en: 'Week Streak',
            uk: 'Тижневий стрік',
          },
        },
      },
      {
        tier: 3,
        threshold: 14,
        celebration: 'modal',
        priority: 80,
        icon: 'flame',
        reward: {
          badgeLabel: {
            en: '14-Day Streak',
            uk: '14-денний стрік',
          },
        },
      },
      {
        tier: 4,
        threshold: 30,
        celebration: 'modal',
        priority: 95,
        icon: 'flame',
        reward: {
          badgeLabel: {
            en: 'Consistency Master',
            uk: 'Майстер стабільності',
          },
          unlockLabel: {
            en: 'Profile highlight',
            uk: 'Акцент у профілі',
          },
        },
      },
    ],
  },
  {
    id: 'xp_total',
    category: 'progress',
    metric: 'xp_total',
    scopeKind: 'all',
    tiers: [
      { tier: 1, threshold: 100, celebration: 'toast', priority: 25, icon: 'zap' },
      { tier: 2, threshold: 500, celebration: 'card', priority: 45, icon: 'zap' },
      {
        tier: 3,
        threshold: 1000,
        celebration: 'card',
        priority: 65,
        icon: 'zap',
        reward: {
          badgeLabel: {
            en: '1K XP',
            uk: '1K XP',
          },
        },
      },
      {
        tier: 4,
        threshold: 5000,
        celebration: 'modal',
        priority: 90,
        icon: 'zap',
        reward: {
          badgeLabel: {
            en: '5K XP',
            uk: '5K XP',
          },
        },
      },
    ],
  },
  {
    id: 'sessions_total',
    category: 'volume',
    metric: 'sessions_total',
    scopeKind: 'all',
    tiers: [
      { tier: 1, threshold: 5, celebration: 'toast', priority: 30, icon: 'trophy' },
      { tier: 2, threshold: 25, celebration: 'card', priority: 50, icon: 'trophy' },
      {
        tier: 3,
        threshold: 100,
        celebration: 'modal',
        priority: 85,
        icon: 'trophy',
        reward: {
          badgeLabel: {
            en: '100 Sessions',
            uk: '100 сесій',
          },
        },
      },
    ],
  },
  {
    id: 'mode_sessions',
    category: 'specialization',
    metric: 'sessions_by_mode',
    scopeKind: 'per-mode',
    tiers: [
      { tier: 1, threshold: 1, celebration: 'toast', priority: 20, icon: 'keyboard' },
      { tier: 2, threshold: 10, celebration: 'card', priority: 40, icon: 'keyboard' },
      {
        tier: 3,
        threshold: 50,
        celebration: 'modal',
        priority: 70,
        icon: 'keyboard',
        reward: {
          badgeLabel: {
            en: 'Mode Specialist',
            uk: 'Спеціаліст режиму',
          },
        },
      },
    ],
  },
];

function getUnlockStorageKey(userId: string) {
  return `${ACHIEVEMENT_UNLOCKS_STORAGE_PREFIX}${userId}`;
}


function sortAchievementUnlocks(unlocks: AchievementUnlockRecord[]) {
  return [...unlocks].sort((left, right) => {
    const dateDiff = new Date(right.unlockedAt).getTime() - new Date(left.unlockedAt).getTime();
    if (dateDiff !== 0) {
      return dateDiff;
    }

    return right.priority - left.priority;
  });
}

function mergeAchievementUnlockCollections(...collections: AchievementUnlockRecord[][]) {
  const merged = new Map<string, AchievementUnlockRecord>();

  collections.flat().forEach((unlock) => {
    const existing = merged.get(unlock.unlockKey);
    if (!existing) {
      merged.set(unlock.unlockKey, unlock);
      return;
    }

    const nextTime = new Date(unlock.unlockedAt).getTime();
    const existingTime = new Date(existing.unlockedAt).getTime();

    if (nextTime > existingTime || (nextTime === existingTime && unlock.priority > existing.priority)) {
      merged.set(unlock.unlockKey, unlock);
    }
  });

  return sortAchievementUnlocks(Array.from(merged.values()));
}

function getMissingAchievementUnlocks(
  source: AchievementUnlockRecord[],
  reference: AchievementUnlockRecord[],
) {
  const referenceKeys = new Set(reference.map((unlock) => unlock.unlockKey));
  return source.filter((unlock) => !referenceKeys.has(unlock.unlockKey));
}

function getModeLabel(mode: TrainingMode, language: AppLanguage) {
  const labels = {
    en: {
      typing: 'Typing Practice',
      'multiple-choice': 'Multiple Choice',
      flashcards: 'Flashcards',
      'speed-verbs': 'Speed Verbs',
    },
    uk: {
      typing: 'Typing Practice',
      'multiple-choice': 'Multiple Choice',
      flashcards: 'Flashcards',
      'speed-verbs': 'Speed Verbs',
    },
  } as const;

  return labels[language][mode];
}

function getModeIcon(mode: TrainingMode): AchievementIcon {
  if (mode === 'typing') return 'keyboard';
  if (mode === 'multiple-choice') return 'pointer';
  if (mode === 'flashcards') return 'cards';
  return 'timer';
}

function getModeBadgeLabel(mode: TrainingMode, language: AppLanguage) {
  const labels = {
    en: {
      typing: 'Typing Specialist',
      'multiple-choice': 'Choice Specialist',
      flashcards: 'Flashcard Specialist',
      'speed-verbs': 'Speed Specialist',
    },
    uk: {
      typing: 'Спеціаліст Typing',
      'multiple-choice': 'Спеціаліст Choice',
      flashcards: 'Спеціаліст Flashcards',
      'speed-verbs': 'Спеціаліст Speed',
    },
  } as const;

  return labels[language][mode];
}

function loadAchievementUnlocks(userId: string): AchievementUnlockRecord[] {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(getUnlockStorageKey(userId));
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as AchievementUnlockRecord[];
    return Array.isArray(parsed) ? sortAchievementUnlocks(parsed) : [];
  } catch (error) {
    console.error('Failed to parse achievement unlocks:', error);
    return [];
  }
}

function saveAchievementUnlocks(userId: string, unlocks: AchievementUnlockRecord[]) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(getUnlockStorageKey(userId), JSON.stringify(sortAchievementUnlocks(unlocks)));
}

function buildStats(sessions: TrainingSession[]): AchievementStats {
  const sessionsByMode = TRAINING_MODES.reduce<Record<TrainingMode, number>>((accumulator, mode) => {
    accumulator[mode] = 0;
    return accumulator;
  }, {
    typing: 0,
    'multiple-choice': 0,
    flashcards: 0,
    'speed-verbs': 0,
  });

  sessions.forEach((session) => {
    sessionsByMode[session.mode] += 1;
  });

  return {
    sessionsTotal: sessions.length,
    xpTotal: calculateTotalXp(sessions),
    currentStreakDays: calculateCurrentStreak(sessions),
    sessionsByMode,
  };
}

function buildUnlockKey(definitionId: string, tier: number, scopeKey: AchievementScopeKey) {
  return `${definitionId}:${tier}:${scopeKey}`;
}

function findDefinition(definitionId: string) {
  return ACHIEVEMENT_DEFINITIONS.find((definition) => definition.id === definitionId) ?? null;
}

function findTier(definitionId: string, tier: number) {
  const definition = findDefinition(definitionId);
  if (!definition) {
    return null;
  }

  const tierDefinition = definition.tiers.find((item) => item.tier === tier);
  if (!tierDefinition) {
    return null;
  }

  return { definition, tierDefinition };
}

function evaluateDefinitionValue(stats: AchievementStats, definition: AchievementDefinition, scopeKey: AchievementScopeKey) {
  if (definition.metric === 'sessions_total') {
    return stats.sessionsTotal;
  }

  if (definition.metric === 'current_streak_days') {
    return stats.currentStreakDays;
  }

  if (definition.metric === 'xp_total') {
    return stats.xpTotal;
  }

  if (definition.metric === 'sessions_by_mode' && scopeKey !== 'all') {
    return stats.sessionsByMode[scopeKey] ?? 0;
  }

  return 0;
}

function evaluateAchievementUnlocks(sessions: TrainingSession[], existingUnlocks: AchievementUnlockRecord[]) {
  const stats = buildStats(sessions);
  const unlockedKeys = new Set(existingUnlocks.map((unlock) => unlock.unlockKey));
  const nextUnlocks: AchievementUnlockRecord[] = [];
  const unlockedAt = new Date().toISOString();

  ACHIEVEMENT_DEFINITIONS.forEach((definition) => {
    const scopeKeys: AchievementScopeKey[] = definition.scopeKind === 'per-mode'
      ? [...TRAINING_MODES]
      : ['all'];

    scopeKeys.forEach((scopeKey) => {
      const resolvedScopeKey = definition.id === 'mode_sessions' ? scopeKey : 'all';
      const value = evaluateDefinitionValue(stats, definition, resolvedScopeKey);

      definition.tiers.forEach((tier) => {
        if (value < tier.threshold) {
          return;
        }

        const unlockKey = buildUnlockKey(definition.id, tier.tier, resolvedScopeKey);
        if (unlockedKeys.has(unlockKey)) {
          return;
        }

        const icon = definition.id === 'mode_sessions' && resolvedScopeKey !== 'all'
          ? getModeIcon(resolvedScopeKey)
          : tier.icon;

        const nextUnlock: AchievementUnlockRecord = {
          unlockKey,
          definitionId: definition.id,
          tier: tier.tier,
          scopeKey: resolvedScopeKey,
          category: definition.category,
          celebration: tier.celebration,
          priority: tier.priority,
          icon,
          threshold: tier.threshold,
          value,
          unlockedAt,
        };

        unlockedKeys.add(unlockKey);
        nextUnlocks.push(nextUnlock);
      });
    });
  });

  return nextUnlocks.sort((left, right) => right.priority - left.priority);
}

function buildRewardLines(unlock: AchievementUnlockRecord, language: AppLanguage, reward?: AchievementRewardDefinition): AchievementRewardView[] {
  const lines: AchievementRewardView[] = [];

  if (unlock.definitionId === 'streak_days') {
    lines.push({
      label: language === 'uk' ? 'Стрік' : 'Streak',
      value: language === 'uk' ? `${unlock.threshold} днів` : `${unlock.threshold} days`,
    });
  }

  if (unlock.definitionId === 'xp_total') {
    lines.push({
      label: language === 'uk' ? 'Загальний XP' : 'Total XP',
      value: `${unlock.threshold} XP`,
    });
  }

  if (unlock.definitionId === 'sessions_total' || unlock.definitionId === 'first_training') {
    lines.push({
      label: language === 'uk' ? 'Сесії' : 'Sessions',
      value: String(unlock.threshold),
    });
  }

  if (unlock.definitionId === 'mode_sessions' && unlock.scopeKey !== 'all') {
    lines.push({
      label: language === 'uk' ? 'Режим' : 'Mode',
      value: getModeLabel(unlock.scopeKey, language),
    });
    lines.push({
      label: language === 'uk' ? 'Сесії в режимі' : 'Mode sessions',
      value: String(unlock.threshold),
    });
  }

  if (reward?.badgeLabel) {
    lines.push({
      label: language === 'uk' ? 'Бейдж' : 'Badge',
      value: reward.badgeLabel[language],
    });
  }

  if (reward?.unlockLabel) {
    lines.push({
      label: language === 'uk' ? 'Відкрито' : 'Unlocked',
      value: reward.unlockLabel[language],
    });
  }

  return lines.slice(0, 3);
}

export function buildAchievementViewModel(unlock: AchievementUnlockRecord, language: AppLanguage): AchievementViewModel {
  const resolved = findTier(unlock.definitionId, unlock.tier);

  if (!resolved) {
    return {
      unlockKey: unlock.unlockKey,
      title: language === 'uk' ? 'Нове досягнення' : 'New achievement',
      description: language === 'uk' ? 'Продовжуйте навчання.' : 'Keep practicing to unlock more milestones.',
      celebration: unlock.celebration,
      priority: unlock.priority,
      icon: unlock.icon,
      category: unlock.category,
      scopeKey: unlock.scopeKey,
      rewardLines: [],
    };
  }

  const { tierDefinition } = resolved;
  const modeLabel = unlock.scopeKey !== 'all' ? getModeLabel(unlock.scopeKey, language) : '';

  if (unlock.definitionId === 'first_training') {
    return {
      unlockKey: unlock.unlockKey,
      title: language === 'uk' ? 'Перша сесія завершена' : 'First session complete',
      description: language === 'uk'
        ? 'Ви завершили перше тренування. Гарний старт уже є.'
        : 'You finished your first practice session. Momentum starts now.',
      celebration: unlock.celebration,
      priority: unlock.priority,
      icon: unlock.icon,
      category: unlock.category,
      scopeKey: unlock.scopeKey,
      rewardLines: buildRewardLines(unlock, language, tierDefinition.reward),
    };
  }

  if (unlock.definitionId === 'streak_days') {
    return {
      unlockKey: unlock.unlockKey,
      title: language === 'uk' ? `${unlock.threshold}-денний стрік` : `${unlock.threshold}-day streak`,
      description: language === 'uk'
        ? 'Ви тримаєте темп. Це вже схоже на звичку, а не випадкову мотивацію.'
        : 'You are building consistency, not just motivation.',
      celebration: unlock.celebration,
      priority: unlock.priority,
      icon: unlock.icon,
      category: unlock.category,
      scopeKey: unlock.scopeKey,
      rewardLines: buildRewardLines(unlock, language, tierDefinition.reward),
    };
  }

  if (unlock.definitionId === 'xp_total') {
    return {
      unlockKey: unlock.unlockKey,
      title: language === 'uk' ? `${unlock.threshold} XP досягнуто` : `${unlock.threshold} XP reached`,
      description: language === 'uk'
        ? 'Ваші тренування вже складаються у відчутний прогрес.'
        : 'Your effort is starting to compound into real progress.',
      celebration: unlock.celebration,
      priority: unlock.priority,
      icon: unlock.icon,
      category: unlock.category,
      scopeKey: unlock.scopeKey,
      rewardLines: buildRewardLines(unlock, language, tierDefinition.reward),
    };
  }

  if (unlock.definitionId === 'sessions_total') {
    return {
      unlockKey: unlock.unlockKey,
      title: language === 'uk' ? `${unlock.threshold} сесій позаду` : `${unlock.threshold} sessions complete`,
      description: language === 'uk'
        ? 'Кількість переходить у впевненість. Ви явно не випадково тут.'
        : 'Volume is turning into confidence. You are showing up consistently.',
      celebration: unlock.celebration,
      priority: unlock.priority,
      icon: unlock.icon,
      category: unlock.category,
      scopeKey: unlock.scopeKey,
      rewardLines: buildRewardLines(unlock, language, tierDefinition.reward),
    };
  }

  return {
    unlockKey: unlock.unlockKey,
    title: language === 'uk' ? `${modeLabel}: новий рубіж` : `${modeLabel}: new milestone`,
    description: language === 'uk'
      ? `Ви прокачуєте саме ${modeLabel.toLowerCase()} і рухаєтесь до спеціалізації.`
      : `You are building real depth in ${modeLabel}.`,
    celebration: unlock.celebration,
    priority: unlock.priority,
    icon: unlock.icon,
    category: unlock.category,
    scopeKey: unlock.scopeKey,
    rewardLines: buildRewardLines(unlock, language, {
      ...tierDefinition.reward,
      badgeLabel: tierDefinition.reward?.badgeLabel
        ? tierDefinition.reward.badgeLabel
        : {
            en: getModeBadgeLabel(unlock.scopeKey as TrainingMode, 'en'),
            uk: getModeBadgeLabel(unlock.scopeKey as TrainingMode, 'uk'),
          },
    }),
  };
}

function hasMatchingSession(left: TrainingSession, right: TrainingSession) {
  return left.id === right.id || (
    left.mode === right.mode &&
    left.sessionMode === right.sessionMode &&
    left.accuracy === right.accuracy &&
    left.durationMinutes === right.durationMinutes &&
    left.verbsLearned === right.verbsLearned &&
    left.xpEarned === right.xpEarned &&
    left.completedAt.toISOString() === right.completedAt.toISOString()
  );
}

function mergeSessionsForAchievement(baseSessions: TrainingSession[], session: TrainingSession) {
  if (baseSessions.some((existingSession) => hasMatchingSession(existingSession, session))) {
    return [...baseSessions].sort((left, right) => right.completedAt.getTime() - left.completedAt.getTime());
  }

  return [...baseSessions, session].sort((left, right) => right.completedAt.getTime() - left.completedAt.getTime());
}

export async function fetchAchievementUnlocks(userId: string): Promise<AchievementUnlockRecord[]> {
  const localUnlocks = loadAchievementUnlocks(userId);

  if (!hasFirebaseConfig) {
    return sortAchievementUnlocks(localUnlocks);
  }

  try {
    const remoteUnlocks = await fetchAchievementUnlocksFromCloud(userId);
    const mergedUnlocks = mergeAchievementUnlockCollections(localUnlocks, remoteUnlocks);

    saveAchievementUnlocks(userId, mergedUnlocks);

    const unlocksToSync = getMissingAchievementUnlocks(mergedUnlocks, remoteUnlocks);
    if (unlocksToSync.length > 0) {
      await syncAchievementUnlocksToCloud(userId, unlocksToSync);
    }

    return mergedUnlocks;
  } catch (error) {
    console.error('Failed to load remote achievements, using local cache:', error);
    return sortAchievementUnlocks(localUnlocks);
  }
}

export function getAchievementCatalogSize() {
  return ACHIEVEMENT_DEFINITIONS.reduce((total, definition) => {
    const scopeMultiplier = definition.scopeKind === 'per-mode' ? TRAINING_MODES.length : 1;
    return total + definition.tiers.length * scopeMultiplier;
  }, 0);
}

export async function processCompletedTrainingAchievements({
  userId,
  session,
}: {
  userId: string;
  session: TrainingSession;
}): Promise<AchievementProcessResult> {
  const localUnlocks = loadAchievementUnlocks(userId);
  let baseSessions: TrainingSession[] = [];
  let remoteUnlocks: AchievementUnlockRecord[] = [];

  try {
    baseSessions = await fetchTrainingSessions(userId);
  } catch (error) {
    console.error('Failed to load sessions for achievements:', error);
    baseSessions = [];
  }

  if (hasFirebaseConfig) {
    try {
      remoteUnlocks = await fetchAchievementUnlocksFromCloud(userId);
    } catch (error) {
      console.error('Failed to load remote achievement unlocks, using local cache:', error);
      remoteUnlocks = [];
    }
  }

  const existingUnlocks = mergeAchievementUnlockCollections(localUnlocks, remoteUnlocks);
  const nextSessions = mergeSessionsForAchievement(baseSessions, session);
  const newUnlocks = evaluateAchievementUnlocks(nextSessions, existingUnlocks);

  const nextUnlockState = mergeAchievementUnlockCollections(existingUnlocks, newUnlocks);
  saveAchievementUnlocks(userId, nextUnlockState);

  if (hasFirebaseConfig) {
    const unlocksToSync = getMissingAchievementUnlocks(nextUnlockState, remoteUnlocks);

    if (unlocksToSync.length > 0) {
      try {
        await syncAchievementUnlocksToCloud(userId, unlocksToSync);
      } catch (error) {
        console.error('Failed to sync achievements to Firestore:', error);
      }
    }
  }

  return {
    unlocks: newUnlocks,
  };
}
